import type { FormQuestion, FormResponse } from "../../types/form";
import {
  INVESTOR_ALPHA_TIME_HORIZONS,
  type InvestorAlphaPreference,
  type InvestorAlphaTimeHorizon,
} from "../../types/investorAlpha";
import { roundToDecimals } from "../../utils/numberUtils";
import type { AlphaIbkrAccountType } from "./alphaIbkrAccountDetails";
import {
  ALPHA_INVESTMENT_TRACK_ANSWERS,
  getInvestmentTrackQuestion,
  getSavedInvestmentTrackPreference,
} from "./alphaInvestmentTrack";

export type AlphaRiskProfileField =
  | "investmentTrack"
  | "plutusInvestment"
  | "totalPortfolio"
  | "plutusPercentage"
  | "investmentExperience"
  | "timeHorizon"
  | "riskScore";

export type AlphaInvestmentExperience =
  | "new-to-investing"
  | "limited-experience"
  | "experienced-decline"
  | "multiple-market-cycles";

export const ALPHA_RISK_SCORES = [3, 6, 8, 9, 10] as const;
export const PERSISTED_ALPHA_RISK_SCORES = [1, 3, 4, 6, 7, 8, 9, 10] as const;

export type AlphaRiskScore = typeof ALPHA_RISK_SCORES[number];
export type PersistedAlphaRiskScore = typeof PERSISTED_ALPHA_RISK_SCORES[number];

export interface AlphaRiskProfileQuestionIds {
  investmentTrack: number;
  plutusInvestment: number;
  totalPortfolio: number;
  plutusPercentage: number;
  investmentExperience: number;
  timeHorizon: number;
  riskScore: number;
}

export interface AlphaRiskProfileAnswers {
  investmentTrack: InvestorAlphaPreference;
  plutusInvestment: string;
  totalPortfolio: string;
  plutusPercentage: string;
  investmentExperience: AlphaInvestmentExperience;
  timeHorizon: InvestorAlphaTimeHorizon;
  riskScore: PersistedAlphaRiskScore;
}

export type SavedAlphaRiskProfileAnswers = Partial<AlphaRiskProfileAnswers>;

export type SavedAlphaRiskMatchAnswers = Partial<Pick<
  AlphaRiskProfileAnswers,
  "investmentTrack" | "plutusInvestment" | "riskScore"
>>;

export const ALPHA_INVESTMENT_EXPERIENCE_CHOICES: ReadonlyArray<{
  value: AlphaInvestmentExperience;
  answer: string;
  score: AlphaRiskScore;
}> = [
  { value: "new-to-investing", answer: "New to investing", score: 8 },
  { value: "limited-experience", answer: "Limited experience", score: 9 },
  {
    value: "experienced-decline",
    answer: "Experienced, including a market decline",
    score: 10,
  },
  {
    value: "multiple-market-cycles",
    answer: "Extensive experience across multiple market cycles",
    score: 10,
  },
];

export const ALPHA_RISK_TIME_HORIZON_CHOICES: ReadonlyArray<{
  value: InvestorAlphaTimeHorizon;
  answer: string;
  score: AlphaRiskScore;
}> = [
  { value: "under-2", answer: "Under 2 years", score: 3 },
  { value: "2-to-5", answer: "2-5 years", score: 6 },
  { value: "5-to-10", answer: "5-10 years", score: 8 },
  { value: "more-than-10", answer: "More than 10 years", score: 10 },
];

export const ALPHA_RISK_OBJECTIVE_SCORES: Readonly<
  Record<InvestorAlphaPreference, AlphaRiskScore>
> = {
  growth: 10,
  balance: 8,
  protect: 6,
};

const QUESTION_CONTRACTS: Record<
  Exclude<AlphaRiskProfileField, "investmentTrack">,
  { name: string; type: "text" | "number" }
> = {
  plutusInvestment: { name: "investment size with plutus", type: "number" },
  totalPortfolio: { name: "size of total portfolio", type: "text" },
  plutusPercentage: { name: "% managed through plutus", type: "text" },
  investmentExperience: { name: "investment experience", type: "text" },
  timeHorizon: { name: "time horizon", type: "text" },
  riskScore: { name: "risk score", type: "number" },
};

const PERSISTED_SCORED_VALUES = new Set<PersistedAlphaRiskScore>(
  PERSISTED_ALPHA_RISK_SCORES
);

export class AlphaRiskProfileContractError extends Error {}

export function isPersistedAlphaRiskScore(
  value: unknown
): value is PersistedAlphaRiskScore {
  return typeof value === "number"
    && PERSISTED_SCORED_VALUES.has(value as PersistedAlphaRiskScore);
}

const normalize = (value: unknown): string => (
  typeof value === "string" ? value.trim().toLowerCase() : ""
);

const getQuestion = (
  questions: FormQuestion[],
  field: Exclude<AlphaRiskProfileField, "investmentTrack">
): FormQuestion => {
  const contract = QUESTION_CONTRACTS[field];
  const matches = questions.filter((question) => (
    normalize(question.question) === contract.name
    && normalize(question.category) === "kyc"
    && normalize(question.subcategory) === "kyc"
  ));

  if (matches.length !== 1) {
    throw new AlphaRiskProfileContractError(
      `Risk profile question contract is invalid for ${field}`
    );
  }

  const question = matches[0];
  if (!Number.isInteger(question.id) || question.id <= 0) {
    throw new AlphaRiskProfileContractError(
      `Risk profile question ID is invalid for ${field}`
    );
  }
  if (normalize(question.type) !== contract.type || question.options.trim() !== "") {
    throw new AlphaRiskProfileContractError(
      `Risk profile question type is invalid for ${field}`
    );
  }
  return question;
};

export function getAlphaRiskProfileQuestionIds(
  questions: FormQuestion[]
): AlphaRiskProfileQuestionIds {
  const ids: AlphaRiskProfileQuestionIds = {
    investmentTrack: getInvestmentTrackQuestion(questions).id,
    plutusInvestment: getQuestion(questions, "plutusInvestment").id,
    totalPortfolio: getQuestion(questions, "totalPortfolio").id,
    plutusPercentage: getQuestion(questions, "plutusPercentage").id,
    investmentExperience: getQuestion(questions, "investmentExperience").id,
    timeHorizon: getQuestion(questions, "timeHorizon").id,
    riskScore: getQuestion(questions, "riskScore").id,
  };

  if (new Set(Object.values(ids)).size !== Object.keys(ids).length) {
    throw new AlphaRiskProfileContractError("Risk profile question IDs must be unique");
  }
  return ids;
}

const responseForQuestion = (
  responses: FormResponse[],
  questionId: number,
  field: AlphaRiskProfileField
): FormResponse | null => {
  const matches = responses.filter((response) => response.questionId === questionId);
  if (matches.length > 1) {
    throw new AlphaRiskProfileContractError(
      `Saved risk profile response contract is invalid for ${field}`
    );
  }
  return matches[0] ?? null;
};

export function normalizeUsdAmount(value: string): string | null {
  const normalized = value.replace(/[$,\s]/g, "");
  if (!/^[1-9]\d*$/.test(normalized)) return null;
  const amount = Number(normalized);
  return Number.isSafeInteger(amount) && amount > 0 ? normalized : null;
}

const parseSavedPlutusInvestment = (response: FormResponse): string => {
  const amount = normalizeUsdAmount(response.answer);
  if (!amount) {
    throw new AlphaRiskProfileContractError("Saved Plutus investment is invalid");
  }
  return amount;
};

const parseSavedRiskScore = (response: FormResponse): PersistedAlphaRiskScore => {
  const score = Number(response.answer);
  if (!isPersistedAlphaRiskScore(score)) {
    throw new AlphaRiskProfileContractError("Saved risk score is invalid");
  }
  return score;
};

// Reads only the saved answers needed to select a managed mix. Other risk
// questionnaire answers do not gate an already-answerable match.
export function getSavedAlphaRiskMatchAnswers(
  questions: FormQuestion[],
  responses: FormResponse[]
): SavedAlphaRiskMatchAnswers {
  const investmentTrackQuestion = getInvestmentTrackQuestion(questions);
  const plutusInvestmentQuestion = getQuestion(questions, "plutusInvestment");
  const riskScoreQuestion = getQuestion(questions, "riskScore");
  const questionIds = [
    investmentTrackQuestion.id,
    plutusInvestmentQuestion.id,
    riskScoreQuestion.id,
  ];
  if (new Set(questionIds).size !== questionIds.length) {
    throw new AlphaRiskProfileContractError(
      "Managed match risk question IDs must be unique"
    );
  }

  const saved: SavedAlphaRiskMatchAnswers = {};
  const investmentTrackResponse = responseForQuestion(
    responses,
    investmentTrackQuestion.id,
    "investmentTrack"
  );
  if (investmentTrackResponse) {
    saved.investmentTrack = getSavedInvestmentTrackPreference(
      investmentTrackQuestion,
      [investmentTrackResponse]
    ) ?? undefined;
  }

  const plutusInvestmentResponse = responseForQuestion(
    responses,
    plutusInvestmentQuestion.id,
    "plutusInvestment"
  );
  if (plutusInvestmentResponse) {
    saved.plutusInvestment = parseSavedPlutusInvestment(plutusInvestmentResponse);
  }

  const riskScoreResponse = responseForQuestion(
    responses,
    riskScoreQuestion.id,
    "riskScore"
  );
  if (riskScoreResponse) {
    saved.riskScore = parseSavedRiskScore(riskScoreResponse);
  }

  return saved;
}

// Plutus does not currently offer products below this amount, so a smaller
// planned investment cannot be onboarded as a customer.
export const PLUTUS_MINIMUM_INVESTMENT_USD = 40000;

export function meetsPlutusMinimumInvestment(value: string): boolean {
  const normalized = normalizeUsdAmount(value);
  return normalized !== null && Number(normalized) >= PLUTUS_MINIMUM_INVESTMENT_USD;
}

const parseOptionalConfigFlag = (value: unknown): boolean | null => {
  if (value === true || value === false) return value;
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return null;
};

// An explicit account config value overrides the questionnaire. Missing or
// invalid isretirement returns null so the saved questionnaire can supply the
// account type instead of silently defaulting the account to taxable.
export function getAlphaConfigAccountType(
  configData: unknown
): AlphaIbkrAccountType | null {
  if (!configData || typeof configData !== "object" || Array.isArray(configData)) {
    return null;
  }
  const data = configData as Record<string, unknown>;
  const isRetirement = parseOptionalConfigFlag(data.isretirement);
  if (isRetirement === null) return null;
  return isRetirement ? "retirement" : "taxable";
}

const parsePercentage = (value: string): string | null => {
  const normalized = value.trim();
  if (!/^(?:\d|[1-9]\d|100)(?:\.\d{1,2})?$/.test(normalized)) return null;
  const percentage = Number(normalized);
  return Number.isFinite(percentage) && percentage >= 0 && percentage <= 100
    ? normalized
    : null;
};

export function calculatePlutusPercentage(
  plutusInvestment: string,
  totalPortfolio: string
): number | null {
  const investment = normalizeUsdAmount(plutusInvestment);
  const portfolio = normalizeUsdAmount(totalPortfolio);
  if (!investment || !portfolio) return null;
  const investmentValue = Number(investment);
  const portfolioValue = Number(portfolio);
  if (portfolioValue < investmentValue) return null;
  return (investmentValue / portfolioValue) * 100;
}

export function serializePlutusPercentage(percentage: number): string {
  const rounded = roundToDecimals(percentage, 2);
  // This is the canonical API value, not UI number formatting.
  // eslint-disable-next-line no-restricted-syntax
  return rounded.toFixed(2);
}

export function calculateAlphaRiskScore(
  investmentExperience: AlphaInvestmentExperience,
  timeHorizon: InvestorAlphaTimeHorizon,
  investmentTrack: InvestorAlphaPreference
): AlphaRiskScore | null {
  const experienceScore = ALPHA_INVESTMENT_EXPERIENCE_CHOICES.find(
    (choice) => choice.value === investmentExperience
  )?.score;
  const horizonScore = ALPHA_RISK_TIME_HORIZON_CHOICES.find(
    (choice) => choice.value === timeHorizon
  )?.score;
  const objectiveScore = ALPHA_RISK_OBJECTIVE_SCORES[investmentTrack];
  if (!experienceScore || !horizonScore || !objectiveScore) {
    return null;
  }
  return Math.min(
    experienceScore,
    horizonScore,
    objectiveScore
  ) as AlphaRiskScore;
}

export function getRiskProfileAnswer(
  field: AlphaRiskProfileField,
  answers: AlphaRiskProfileAnswers
): string {
  if (field === "investmentTrack") {
    return ALPHA_INVESTMENT_TRACK_ANSWERS[answers.investmentTrack];
  }
  if (field === "plutusInvestment") return answers.plutusInvestment;
  if (field === "totalPortfolio") return answers.totalPortfolio;
  if (field === "plutusPercentage") return answers.plutusPercentage;
  if (field === "investmentExperience") {
    return ALPHA_INVESTMENT_EXPERIENCE_CHOICES.find(
      (choice) => choice.value === answers.investmentExperience
    )?.answer ?? "";
  }
  if (field === "timeHorizon") {
    return ALPHA_RISK_TIME_HORIZON_CHOICES.find(
      (choice) => choice.value === answers.timeHorizon
    )?.answer ?? "";
  }
  return String(answers.riskScore);
}

export function getSavedAlphaRiskProfileAnswers(
  questions: FormQuestion[],
  questionIds: AlphaRiskProfileQuestionIds,
  responses: FormResponse[]
): SavedAlphaRiskProfileAnswers {
  const saved: SavedAlphaRiskProfileAnswers = {};
  const investmentTrackQuestion = getInvestmentTrackQuestion(questions);
  const investmentTrackResponse = responseForQuestion(
    responses,
    questionIds.investmentTrack,
    "investmentTrack"
  );
  if (investmentTrackResponse) {
    saved.investmentTrack = getSavedInvestmentTrackPreference(
      investmentTrackQuestion,
      [investmentTrackResponse]
    ) ?? undefined;
  }

  const plutusInvestment = responseForQuestion(
    responses,
    questionIds.plutusInvestment,
    "plutusInvestment"
  );
  if (plutusInvestment) {
    saved.plutusInvestment = parseSavedPlutusInvestment(plutusInvestment);
  }

  const totalPortfolio = responseForQuestion(
    responses,
    questionIds.totalPortfolio,
    "totalPortfolio"
  );
  if (totalPortfolio) {
    const amount = normalizeUsdAmount(totalPortfolio.answer);
    if (!amount) throw new AlphaRiskProfileContractError("Saved total portfolio is invalid");
    saved.totalPortfolio = amount;
  }

  const plutusPercentage = responseForQuestion(
    responses,
    questionIds.plutusPercentage,
    "plutusPercentage"
  );
  if (plutusPercentage) {
    const percentage = parsePercentage(plutusPercentage.answer);
    if (!percentage) throw new AlphaRiskProfileContractError("Saved Plutus percentage is invalid");
    saved.plutusPercentage = percentage;
  }

  const investmentExperience = responseForQuestion(
    responses,
    questionIds.investmentExperience,
    "investmentExperience"
  );
  if (investmentExperience) {
    const choice = ALPHA_INVESTMENT_EXPERIENCE_CHOICES.find(
      (candidate) => candidate.answer === investmentExperience.answer
    );
    if (!choice) throw new AlphaRiskProfileContractError("Saved investment experience is invalid");
    saved.investmentExperience = choice.value;
  }

  const timeHorizon = responseForQuestion(
    responses,
    questionIds.timeHorizon,
    "timeHorizon"
  );
  if (timeHorizon) {
    const choice = ALPHA_RISK_TIME_HORIZON_CHOICES.find(
      (candidate) => candidate.answer === timeHorizon.answer
    );
    if (!choice) throw new AlphaRiskProfileContractError("Saved time horizon is invalid");
    saved.timeHorizon = choice.value;
  }

  const riskScore = responseForQuestion(
    responses,
    questionIds.riskScore,
    "riskScore"
  );
  if (riskScore) {
    saved.riskScore = parseSavedRiskScore(riskScore);
  }

  return saved;
}

export function getCompleteSavedAlphaRiskProfile(
  questions: FormQuestion[],
  questionIds: AlphaRiskProfileQuestionIds,
  responses: FormResponse[]
): AlphaRiskProfileAnswers | null {
  const saved = getSavedAlphaRiskProfileAnswers(questions, questionIds, responses);
  if (
    !saved.investmentTrack
    || !saved.plutusInvestment
    || !saved.totalPortfolio
    || !saved.plutusPercentage
    || !saved.investmentExperience
    || !saved.timeHorizon
    || !saved.riskScore
    || !INVESTOR_ALPHA_TIME_HORIZONS.includes(saved.timeHorizon)
  ) return null;

  const percentage = calculatePlutusPercentage(
    saved.plutusInvestment,
    saved.totalPortfolio
  );
  if (
    percentage === null
    || serializePlutusPercentage(percentage) !== serializePlutusPercentage(
      Number(saved.plutusPercentage)
    )
  ) return null;

  return saved as AlphaRiskProfileAnswers;
}
